import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { VerifiedUserOutlined, PolicyOutlined, QrCodeScannerOutlined, GavelOutlined, CheckCircleOutline, ArrowForward, InfoOutlined, WarningAmberOutlined, StorefrontOutlined, FactCheckOutlined } from '@mui/icons-material';
import '../styles/SeoPages.css';
import '../styles/common.css';

const BisHallmarkingGuidePage = () => {
    useEffect(() => {
        document.title = "BIS Hallmarking Guide | Certified Gold Jewellery at MAB Jewels";

        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Understand BIS hallmarking, gold purity marks, and how MAB Jewels ensures certified gold jewellery in Rajasthan.");
        }
    }, []);

    return (
        <div className="page-container seo-page">
            <div className="seo-content-wrapper">
                <h1>BIS Hallmarking Guide – Certified Gold Jewellery at MAB Jewels</h1>

                <p className="seo-intro">
                    Buying gold jewellery is not just an emotional decision but also a financial one. To protect customers from adulteration and false purity claims, the Government of India introduced BIS Hallmarking.
                </p>
                <p>
                    At <strong>MAB Jewels</strong>, we strictly follow BIS hallmarking standards to ensure every customer receives pure, certified, and trustworthy gold jewellery.
                </p>

                <h2>What Is BIS Hallmarking?</h2>
                <p>
                    BIS Hallmarking is a certification system regulated by the <strong>Bureau of Indian Standards (BIS)</strong>. It verifies the purity of gold jewellery through approved assaying and hallmarking centres. A BIS hallmark confirms that the gold jewellery meets national quality standards.
                </p>

                <h2>Components of BIS Hallmark (NEW SYSTEM)</h2>
                <div style={{ background: '#f8f9fa', padding: '25px', borderRadius: '12px', border: '1px solid #d4af37', marginBottom: '30px' }}>
                    <p style={{ marginTop: 0, fontWeight: 'bold', color: '#666' }}>Every BIS hallmarked gold jewellery includes the following marks:</p>

                    <div className="seo-steps-list" style={{ marginTop: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
                            <PolicyOutlined sx={{ fontSize: 32, color: '#d4af37', mr: 2, mt: 0.5 }} />
                            <div>
                                <h4 style={{ margin: '0 0 5px 0', color: '#1a1a1a' }}>1. BIS Logo</h4>
                                <p style={{ margin: 0, fontSize: '0.95rem' }}>Indicates official certification by the Bureau of Indian Standards.</p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '20px' }}>
                            <FactCheckOutlined sx={{ fontSize: 32, color: '#d4af37', mr: 2, mt: 0.5 }} />
                            <div>
                                <h4 style={{ margin: '0 0 5px 0', color: '#1a1a1a' }}>2. Purity / Fineness Mark</h4>
                                <p style={{ margin: 0, fontSize: '0.95rem' }}>Examples: <strong>22K916</strong> (91.6% Pure), <strong>18K750</strong> (75.0% Pure).</p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            <QrCodeScannerOutlined sx={{ fontSize: 32, color: '#d4af37', mr: 2, mt: 0.5 }} />
                            <div>
                                <h4 style={{ margin: '0 0 5px 0', color: '#1a1a1a' }}>3. HUID (Hallmark Unique Identification Number)</h4>
                                <p style={{ margin: 0, fontSize: '0.95rem' }}>A 6-digit alphanumeric code that allows traceability and verification.</p>
                            </div>
                        </div>
                    </div>
                </div>

                <h2>Common Gold Purity Standards</h2>
                <div style={{ overflowX: 'auto', marginBottom: '30px' }}>
                    <table className="seo-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                        <thead>
                            <tr style={{ background: '#f0f0f0' }}>
                                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Gold Purity</th>
                                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Fineness</th>
                                <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Usage</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}><strong>24K</strong></td>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}>999</td>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}>Coins & bars</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}><strong>22K</strong></td>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}>916</td>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}>Jewellery</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}><strong>18K</strong></td>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}>750</td>
                                <td style={{ padding: '12px', border: '1px solid #ddd' }}>Lightweight & diamond jewellery</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div className="seo-highlight-box">
                    <p><InfoOutlined sx={{ color: '#d4af37', verticalAlign: 'middle', mr: 1 }} /> At MAB Jewels, we clearly explain the purity before every purchase.</p>
                </div>

                <h2>Why BIS Hallmarking Is Important</h2>
                <ul className="seo-check-list">
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Confirms gold purity</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Prevents fraud and under-purity</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Protects customer investment</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Builds long-term trust</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Mandatory for gold jewellery sales in India</li>
                </ul>
                <p style={{ color: '#d32f2f', fontWeight: 'bold' }}><WarningAmberOutlined sx={{ verticalAlign: 'middle', mr: 1 }} /> Buying non-hallmarked jewellery can result in financial loss.</p>

                <h2>How MAB Jewels Ensures BIS Compliance</h2>
                <p>At MAB Jewels, we ensure:</p>
                <ul className="seo-check-list">
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> BIS Hallmark certified jewellery only</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> HUID number on every gold item</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Transparent invoices mentioning purity</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> Customer education before purchase</li>
                </ul>
                <p>This commitment has made MAB Jewels a <Link to="/trusted-jewellery-store-jodhpur">trusted jewellery name in Rajasthan</Link>.</p>

                <h2>How to Verify BIS Hallmark Jewellery</h2>
                <p>Customers can verify hallmark jewellery by:</p>
                <div className="seo-simple-list">
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}><ArrowForward sx={{ color: '#d4af37', mr: 1, fontSize: 18 }} /> Checking the HUID on the BIS Care App</li>
                        <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}><ArrowForward sx={{ color: '#d4af37', mr: 1, fontSize: 18 }} /> Matching purity marks on jewellery</li>
                        <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}><ArrowForward sx={{ color: '#d4af37', mr: 1, fontSize: 18 }} /> Verifying invoice details</li>
                    </ul>
                </div>
                <p>Our team assists customers in understanding these steps.</p>

                <h2>FAQs – BIS Hallmarking</h2>
                <div className="seo-faq-section">
                    <div className="seo-faq-item">
                        <h4>Q1. Is BIS hallmark compulsory in India?</h4>
                        <p>Yes. BIS hallmarking is mandatory for gold jewellery sales.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q2. Can BIS hallmarked jewellery be fake?</h4>
                        <p>No. BIS hallmarking ensures government-certified purity.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Q3. Does MAB Jewels sell only hallmarked gold?</h4>
                        <p>Yes. All gold jewellery at MAB Jewels is BIS Hallmark certified.</p>
                    </div>
                </div>

                <div className="internal-links-section" style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>Explore More</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/gold-jewellery-jodhpur">Gold Jewellery in Jodhpur</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/how-to-check-gold-purity-rajasthan">How to Check Gold Purity</Link></li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}><ArrowForward sx={{ color: '#d4af37', mr: 1 }} /> <Link to="/trusted-jewellery-store-jodhpur">Trusted Jewellery Store in Jodhpur</Link></li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default BisHallmarkingGuidePage;
